/**
 * Enrollment Store
 *
 * A production-quality Redux store that manages user enrollment state for the frontend.
 * Follows WordPress data patterns with resolvers, selectors, and actions.
 * Similar to how @wordpress/core-data works.
 *
 * This store is used on the FRONTEND to query actual enrollment status and render blocks accordingly.
 * This is separate from loyalty-preview-store.ts which is only used in the EDITOR for previewing.
 */
import { createReduxStore, register, dispatch, select } from '@wordpress/data';
import type { StoreDescriptor } from '@wordpress/data';
import apiFetch from '@wordpress/api-fetch';
import { parseEnrollmentError } from '../utils/enrollment-errors';
import { getTranslatedText } from '../utils/translated-text';



// ============================================================================
// Types
// ============================================================================

/**
 * Enrollment status representing the user's current state
 */
export type EnrollmentStatus = 'guest' | 'loggedInNotEnrolled' | 'enrolled';

/**
 * Contact data from the REST API
 * Based on the structure returned by ContactEndpoint.php
 */
export interface Contact {
	uuid: string;
	id?: number;
	email: string;
	balance: {
		prepaid: number;
		credits: number;
	};
	attributes?: Record<string, any>;
	// Add other fields as needed from your API
}

/**
 * Tier data from the REST API
 */
export interface Tier {
	id: string;
	name: string;
	position: number;
	media?: {
		type: string;
		value: string;
	} | null;
	description?: string | null;
}

/**
 * Claimed reward log entry
 */
export interface ClaimedReward {
	id?: number;
	earn_rule_id: string;
	wp_user_id?: number;
	credits?: number;
	claimed_at?: string;
	// Add other fields as needed from your API
}

/**
 * Full contact response from GET /leat/v1/contact
 * This matches the structure in ContactEndpoint.php:177-183
 */
export interface ContactResponse {
	contact: Contact;
	claimedRewards: ClaimedReward[];
	tier: Tier | null;
}

/**
 * Join program response
 */
export interface JoinProgramResponse {
	contact: Contact;
	success: boolean;
	message?: string;
}

/**
 * State shape for the enrollment store
 */
interface EnrollmentState {
	readonly contact: Contact | null;
	readonly tier: Tier | null;
	readonly claimedRewards: ClaimedReward[];
	readonly status: EnrollmentStatus;
	readonly isLoading: boolean;
	readonly error: string | null;
	readonly hasHydrated: boolean;
}

/**
 * Action types
 */
type SetContactAction = {
	type: 'SET_CONTACT';
	contact: Contact | null;
};

type SetTierAction = {
	type: 'SET_TIER';
	tier: Tier | null;
};

type SetClaimedRewardsAction = {
	type: 'SET_CLAIMED_REWARDS';
	claimedRewards: ClaimedReward[];
};

type SetEnrollmentStatusAction = {
	type: 'SET_ENROLLMENT_STATUS';
	status: EnrollmentStatus;
};

type SetLoadingAction = {
	type: 'SET_LOADING';
	isLoading: boolean;
};

type SetErrorAction = {
	type: 'SET_ERROR';
	error: string | null;
};

type SetHasHydratedAction = {
	type: 'SET_HAS_HYDRATED';
	hasHydrated: boolean;
};

type EnrollmentAction =
	| SetContactAction
	| SetTierAction
	| SetClaimedRewardsAction
	| SetEnrollmentStatusAction
	| SetLoadingAction
	| SetErrorAction
	| SetHasHydratedAction;


// ============================================================================
// Implementation
// ============================================================================

/**
 * Get initial state from server-rendered data if available
 * This prevents the 0 → actual_credits animation flash on page load
 */
function getInitialState(): EnrollmentState {
	const serverData = window.leatEnrollmentData;

	if (serverData) {
		return {
			contact: serverData.contact || null,
			tier: null, // Tier is fetched separately
			claimedRewards: serverData.claimedRewards || [],
			status: serverData.status || 'guest',
			isLoading: false,
			error: null,
			hasHydrated: false,
		};
	}

	return {
		contact: null,
		tier: null,
		claimedRewards: [],
		status: 'guest',
		isLoading: false,
		error: null,
		hasHydrated: false,
	};
}

const DEFAULT_STATE: EnrollmentState = getInitialState();

/**
 * Actions - Synchronous state updates
 */
const actions = {
	setContact(contact: Contact | null): SetContactAction {
		return {
			type: 'SET_CONTACT',
			contact,
		};
	},

	setTier(tier: Tier | null): SetTierAction {
		return {
			type: 'SET_TIER',
			tier,
		};
	},

	setClaimedRewards(claimedRewards: ClaimedReward[]): SetClaimedRewardsAction {
		return {
			type: 'SET_CLAIMED_REWARDS',
			claimedRewards,
		};
	},

	setEnrollmentStatus(status: EnrollmentStatus): SetEnrollmentStatusAction {
		return {
			type: 'SET_ENROLLMENT_STATUS',
			status,
		};
	},

	setLoading(isLoading: boolean): SetLoadingAction {
		return {
			type: 'SET_LOADING',
			isLoading,
		};
	},

	setError(error: string | null): SetErrorAction {
		return {
			type: 'SET_ERROR',
			error,
		};
	},

	setHasHydrated(hasHydrated: boolean): SetHasHydratedAction {
		return {
			type: 'SET_HAS_HYDRATED',
			hasHydrated,
		};
	},

};

/**
 * Selectors - Read state
 */
const selectors = {
	getContact(state: EnrollmentState): Contact | null {
		return state.contact;
	},

	getTier(state: EnrollmentState): Tier | null {
		return state.tier;
	},

	getClaimedRewards(state: EnrollmentState): ClaimedReward[] {
		return state.claimedRewards;
	},

	getEnrollmentStatus(state: EnrollmentState): EnrollmentStatus {
		return state.status;
	},

	isEnrolled(state: EnrollmentState): boolean {
		return state.status === 'enrolled';
	},

	isLoggedIn(state: EnrollmentState): boolean {
		return state.status !== 'guest';
	},

	isGuest(state: EnrollmentState): boolean {
		return state.status === 'guest';
	},

	isLoading(state: EnrollmentState): boolean {
		return state.isLoading;
	},

	getError(state: EnrollmentState): string | null {
		return state.error;
	},

	getCredits(state: EnrollmentState): number {
		return state.contact?.balance?.credits ?? 0;
	},

	getCreditsCurrency(state: EnrollmentState): string {
		// Read from server-rendered enrollment data first.
		const serverCurrency = window.leatEnrollmentData?.creditsCurrency;
		if (serverCurrency) {
			return serverCurrency;
		}

		// Fall back to settings.
		const creditsNameSetting = window.leat?.settings?.credits_name;
		if (creditsNameSetting) {
			return getTranslatedText(creditsNameSetting as unknown as Record<string, string>) || 'Credits';
		}

		return 'Credits';
	},

	getHasHydrated(state: EnrollmentState): boolean {
		return state.hasHydrated;
	},
};

/**
 * Resolvers - Async data fetching
 * These are called by useSelect when data is needed
 */
const createResolvers = (storeDispatch: typeof import('@wordpress/data').dispatch, storeSelect: typeof import('@wordpress/data').select) => {
	// Helper function to get store actions
	const getStoreActions = () => storeDispatch(ENROLLMENT_STORE_NAME) as typeof actions;

	// Helper function to get store selectors
	const getStoreSelectors = () => storeSelect(ENROLLMENT_STORE_NAME) as typeof selectors;

	/**
	 * Check if we should use server-rendered data
	 */
	const shouldUseServerData = (): boolean => {
		const serverData = window.leatEnrollmentData;
		const hasHydrated = getStoreSelectors().getHasHydrated();
		return Boolean(serverData?.contact && !hasHydrated);
	};

	/**
	 * Use server-rendered data without making an API call
	 */
	const useServerRenderedData = (): void => {
		const { setHasHydrated } = getStoreActions();
		// Data is already in store from getInitialState()
		setHasHydrated(true);
	};

	/**
	 * Check if user is logged in
	 */
	const isUserLoggedIn = (): boolean => {
		return window.leat?.loggedIn === '1';
	};

	/**
	 * Set guest status in the store
	 */
	const setGuestStatus = (): void => {
		const { setContact, setTier, setClaimedRewards, setEnrollmentStatus } = getStoreActions();
		setContact(null);
		setTier(null);
		setClaimedRewards([]);
		setEnrollmentStatus('guest');
	};

	/**
	 * Fetch and set contact data from API
	 */
	const fetchAndSetContactData = async (): Promise<void> => {
		const { setContact, setTier, setClaimedRewards, setEnrollmentStatus } = getStoreActions();

		try {
			const response = await apiFetch<ContactResponse>({
				path: '/leat/v1/contact',
			});

			setContact(response.contact);
			setTier(response.tier);
			setClaimedRewards(response.claimedRewards);
			setEnrollmentStatus('enrolled');
		} catch (error) {
			// If error is a Response object, parse it first
			if (error instanceof Response) {
				const errorData = await error.json();
				throw errorData;
			}
			throw error;
		}
	};

	/**
	 * Handle enrollment error and set appropriate status
	 */
	const handleEnrollmentError = (error: unknown): void => {
		const { setContact, setTier, setClaimedRewards, setEnrollmentStatus, setError } = getStoreActions();
		const parsedError = parseEnrollmentError(error);

		switch (parsedError.type) {
			case 'unauthorized':
				// User is not logged in (guest)
				setGuestStatus();
				break;

			case 'not_enrolled':
				// User is logged in but not enrolled yet
				setContact(null);
				setTier(null);
				setClaimedRewards([]);
				setEnrollmentStatus('loggedInNotEnrolled');
				break;

			default:
				// Unknown error - log it
				console.error('[Leat Enrollment Store] Unexpected error:', error);
				setError(parsedError.message);
				break;
		}
	};

	return {
		/**
		 * Fetch contact data from the REST API
		 * This resolver is automatically called when getContact is used in useSelect
		 */
		async getContact() {
			const { setLoading, setError } = getStoreActions();

			// Check if we already have server-rendered data
			// Use it for immediate hydration, but still fetch from API to get tier
			if (shouldUseServerData()) {
				useServerRenderedData();
				// Continue to fetch from API to get tier data
				// Don't set loading to true to avoid flicker since we have initial data
			} else {
				setLoading(true);
			}
			setError(null);

			try {
				// Check if user is logged in before making the API call
				if (!isUserLoggedIn()) {
					setGuestStatus();
					return;
				}

				// Fetch contact data from REST API (includes tier)
				await fetchAndSetContactData();
			} catch (error: unknown) {
				handleEnrollmentError(error);
			} finally {
				setLoading(false);
			}
		},

		/**
		 * Fetch enrollment status
		 * This is a convenience resolver that calls getContact internally
		 */
		async getEnrollmentStatus() {
			return this.getContact();
		},
	};
};

/**
 * Reducer - Handle state updates
 */
function reducer(state = DEFAULT_STATE, action: EnrollmentAction): EnrollmentState {
	switch (action.type) {
		case 'SET_CONTACT':
			return {
				...state,
				contact: action.contact,
			};
		case 'SET_TIER':
			return {
				...state,
				tier: action.tier,
			};
		case 'SET_CLAIMED_REWARDS':
			return {
				...state,
				claimedRewards: action.claimedRewards,
			};
		case 'SET_ENROLLMENT_STATUS':
			return {
				...state,
				status: action.status,
			};
		case 'SET_LOADING':
			return {
				...state,
				isLoading: action.isLoading,
			};
		case 'SET_ERROR':
			return {
				...state,
				error: action.error,
			};
		case 'SET_HAS_HYDRATED':
			return {
				...state,
				hasHydrated: action.hasHydrated,
			};
		default:
			return state;
	}
}

// ============================================================================
// Store Registration
// ============================================================================

export const ENROLLMENT_STORE_NAME = 'leat/enrollment' as const;

const storeConfig = {
	reducer,
	actions,
	selectors,
	resolvers: createResolvers(dispatch, select),
};

export const enrollmentStore: StoreDescriptor<typeof storeConfig> = createReduxStore(
	ENROLLMENT_STORE_NAME,
	storeConfig
);

register(enrollmentStore);

// ============================================================================
// Type Exports
// ============================================================================

/**
 * Inferred types from implementation - no need to maintain separate interface definitions
 */
export type EnrollmentActions = typeof actions;
export type EnrollmentSelectors = typeof selectors;
export type EnrollmentResolvers = ReturnType<typeof createResolvers>;

/**
 * Complete store configuration type
 */
export interface EnrollmentStoreConfig {
	state: EnrollmentState;
	actions: EnrollmentActions;
	selectors: EnrollmentSelectors;
	resolvers: EnrollmentResolvers;
}
